import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Check, Loader2, Plus, Trash2, Save, CheckCircle2, Circle, Eye,
} from 'lucide-react';
import { templateApi, profilesApi } from '../../services/api';
import { templateToHtml } from '../../lib/templateToHtml';
import type {
  ExamCategory, ReportTemplate, ReportProfile, StyleConfig, LanguageConfig,
} from '../../types';
import SectionBuilder, { type EditableSection } from './SectionBuilder';

const DEFAULT_SECTIONS: EditableSection[] = [
  { name: 'Examination', fixed_prefix: '', normal_default: '', slot_name: '', conditional: false, include: true },
  { name: 'Clinical History', fixed_prefix: '', normal_default: '', slot_name: 'clinical_history', conditional: false, include: true },
  { name: 'Technique', fixed_prefix: '', normal_default: '', slot_name: 'technique', conditional: true, include: true },
  { name: 'Comparison', fixed_prefix: '', normal_default: 'None.', slot_name: 'comparison', conditional: false, include: true },
  { name: 'Findings', fixed_prefix: '', normal_default: '', slot_name: 'findings', conditional: false, include: true },
  { name: 'Conclusion', fixed_prefix: '', normal_default: '', slot_name: 'conclusion', conditional: false, include: true },
];

const DEFAULT_STYLE: StyleConfig = {
  structure: 'narrative', conclusion_style: 'numbered', heading_case: 'title', use_subheadings: false,
};

function sectionsFromTemplate(t?: ReportTemplate | null): EditableSection[] {
  const secs = (t?.template_config as { sections?: any[] } | undefined)?.sections;
  if (!secs || !secs.length) return DEFAULT_SECTIONS.map((s) => ({ ...s }));
  return secs.map((s) => ({
    name: s.name || '',
    fixed_prefix: s.fixed_prefix || '',
    normal_default: s.normal_default || '',
    slot_name: s.slot_name || '',
    conditional: !!s.conditional,
    include: true,
  }));
}

function toConfigSection(s: EditableSection) {
  return {
    name: s.name,
    fixed_prefix: s.fixed_prefix,
    normal_default: s.normal_default,
    slot_name: s.slot_name,
    conditional: s.conditional,
  };
}

function applyOrder(sections: EditableSection[], order?: string[]): EditableSection[] {
  if (!order || !order.length) return sections;
  const idx = (n: string) => {
    const i = order.indexOf(n);
    return i === -1 ? 999 : i;
  };
  return [...sections].sort((a, b) => idx(a.name) - idx(b.name));
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<ExamCategory[]>([]);
  const [configured, setConfigured] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [loadingEditor, setLoadingEditor] = useState(false);

  // Editor state for the selected category
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [baseTemplateId, setBaseTemplateId] = useState<string>('');
  const [customTemplateId, setCustomTemplateId] = useState<string | undefined>(undefined);
  const [sections, setSections] = useState<EditableSection[]>([]);
  const [style, setStyle] = useState<StyleConfig>(DEFAULT_STYLE);
  const [language, setLanguage] = useState<LanguageConfig>({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Initial load: categories + which already have a profile
  useEffect(() => {
    (async () => {
      const [cats, profiles] = await Promise.all([
        templateApi.categories().catch(() => [] as ExamCategory[]),
        profilesApi.list().catch(() => [] as ReportProfile[]),
      ]);
      setCategories(cats);
      setConfigured(new Set(profiles.map((p) => p.exam_category)));
    })();
  }, []);

  const grouped = useMemo(() => {
    const g: Record<string, ExamCategory[]> = {};
    for (const c of categories) (g[c.modality] ||= []).push(c);
    return g;
  }, [categories]);

  const selectCategory = async (key: string) => {
    setSelected(key);
    setLoadingEditor(true);
    setSavedAt(null);
    try {
      const [eff, tpls] = await Promise.all([
        profilesApi.getEffective(key),
        templateApi.list({ exam_category: key }).catch(() => [] as ReportTemplate[]),
      ]);
      setTemplates(tpls);

      let baseTpl: ReportTemplate | null = null;
      let custom: string | undefined;
      if (eff.template_id) {
        const existing = tpls.find((t) => t.id === eff.template_id)
          || (await templateApi.get(eff.template_id).catch(() => null));
        if (existing) {
          baseTpl = existing;
          if (existing.source === 'user') custom = existing.id;
        }
      }
      if (!baseTpl) {
        // Prefer a RAAS reference template as the starting point.
        baseTpl = tpls.find((t) => t.source === 'raas') || tpls[0] || null;
      }
      setBaseTemplateId(baseTpl?.id || '');
      setCustomTemplateId(custom);
      setSections(applyOrder(sectionsFromTemplate(baseTpl), eff.style_config?.section_order));
      setStyle({ ...DEFAULT_STYLE, ...(eff.style_config || {}) });
      setLanguage(eff.language_config || {});
    } finally {
      setLoadingEditor(false);
    }
  };

  const onPickBaseTemplate = async (id: string) => {
    setBaseTemplateId(id);
    setCustomTemplateId(undefined); // switching base starts a fresh custom layout
    const t = templates.find((x) => x.id === id) || (id ? await templateApi.get(id).catch(() => null) : null);
    setSections(sectionsFromTemplate(t));
  };

  const currentCategory = categories.find((c) => c.key === selected);

  const previewHtml = useMemo(() => {
    const included = sections.filter((s) => s.include).map(toConfigSection);
    return templateToHtml({ template_config: { sections: included } } as unknown as ReportTemplate);
  }, [sections]);

  const save = async () => {
    if (!selected || !currentCategory) return;
    setSaving(true);
    try {
      const included = sections.filter((s) => s.include);
      const template_config = {
        sections: included.map(toConfigSection),
        source_format: 'onboarding',
        notes: 'Built via the onboarding wizard.',
      };

      // Persist the customised layout as a user-owned template.
      let templateId = customTemplateId;
      if (templateId) {
        await templateApi.update(templateId, { template_config, exam_category: selected });
      } else {
        const created = await templateApi.create({
          name: `${currentCategory.display_name} — My Layout`,
          modality: currentCategory.modality,
          exam_category: selected,
          template_config,
          template_structure: {},
          description: 'Created via onboarding.',
        });
        templateId = created.id;
        setCustomTemplateId(templateId);
      }

      const style_config: StyleConfig = { ...style, section_order: included.map((s) => s.name) };
      await profilesApi.upsert(selected, { template_id: templateId, style_config, language_config: language });

      setConfigured((prev) => new Set(prev).add(selected));
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-surface-950 flex flex-col">
      {/* Header */}
      <header className="flex items-center gap-3 px-4 h-14 border-b border-gray-200 dark:border-surface-800 bg-white dark:bg-surface-900 shrink-0">
        <button
          onClick={() => navigate('/app')}
          className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft size={16} /> Back to reporting
        </button>
        <h1 className="text-base font-semibold text-gray-900 dark:text-white ml-2">Reporting Onboarding</h1>
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
          {configured.size} / {categories.length} exam types configured
        </span>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Category list */}
        <aside className="w-64 border-r border-gray-200 dark:border-surface-800 bg-white dark:bg-surface-900 overflow-y-auto shrink-0">
          {Object.entries(grouped).map(([modality, cats]) => (
            <div key={modality}>
              <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 sticky top-0 bg-white dark:bg-surface-900">
                {modality}
              </div>
              {cats.map((c) => (
                <button
                  key={c.key}
                  onClick={() => selectCategory(c.key)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                    selected === c.key
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-surface-850'
                  }`}
                >
                  {configured.has(c.key) ? (
                    <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                  ) : (
                    <Circle size={14} className="text-gray-300 dark:text-gray-600 shrink-0" />
                  )}
                  <span className="truncate">{c.display_name}</span>
                </button>
              ))}
            </div>
          ))}
        </aside>

        {/* Editor */}
        <main className="flex-1 overflow-y-auto">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 gap-2">
              <Eye size={40} className="opacity-40" />
              <p className="text-sm">Pick an exam type on the left to configure how its reports should look.</p>
            </div>
          ) : loadingEditor ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 size={24} className="animate-spin text-primary-500" />
            </div>
          ) : (
            <div className="max-w-5xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* LEFT: config */}
              <div className="space-y-6">
                {/* Base template */}
                <Panel title="Base template" subtitle="Start from a library template; then customise below.">
                  <select
                    value={baseTemplateId}
                    onChange={(e) => onPickBaseTemplate(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-surface-850 border border-gray-300 dark:border-surface-800 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Start from scratch</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}{t.source === 'raas' ? ' (RAAS)' : ''}
                      </option>
                    ))}
                  </select>
                </Panel>

                {/* Sections (drag to reorder) */}
                <Panel title="Sections & layout" subtitle="Drag the handles to reorder. Edit headings and the canonical normal text.">
                  <SectionBuilder sections={sections} setSections={setSections} />
                </Panel>

                {/* Style */}
                <Panel title="Style" subtitle="How the report is formatted.">
                  <div className="space-y-3 text-sm">
                    <Choice label="Findings layout" value={style.structure || 'narrative'}
                      options={[['narrative', 'Narrative prose'], ['itemized', 'Itemized list']]}
                      onChange={(v) => setStyle({ ...style, structure: v as StyleConfig['structure'] })} />
                    <Choice label="Conclusion" value={style.conclusion_style || 'numbered'}
                      options={[['numbered', 'Numbered list'], ['prose', 'Single paragraph']]}
                      onChange={(v) => setStyle({ ...style, conclusion_style: v as StyleConfig['conclusion_style'] })} />
                    <Choice label="Headings" value={style.heading_case || 'title'}
                      options={[['title', 'Title Case'], ['upper', 'UPPERCASE']]}
                      onChange={(v) => setStyle({ ...style, heading_case: v as StyleConfig['heading_case'] })} />
                    <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={!!style.use_subheadings}
                        onChange={(e) => setStyle({ ...style, use_subheadings: e.target.checked })}
                        className="w-4 h-4 text-primary-600 rounded" />
                      Use organ/system subheadings within Findings
                    </label>
                  </div>
                </Panel>

                {/* Language */}
                <Panel title="Language & syntax" subtitle="Your house vocabulary and phrasing.">
                  <div className="space-y-4">
                    <PairEditor label="Vocabulary substitutions" aLabel="write this…" bLabel="…instead of this"
                      rows={(language.vocabulary || []).map((v) => [v.to, v.from])}
                      onChange={(rows) => setLanguage({ ...language, vocabulary: rows.map(([to, from]) => ({ to, from })) })} />
                    <StringList label="Preferred phrases"
                      items={language.preferred_phrases || []}
                      onChange={(items) => setLanguage({ ...language, preferred_phrases: items })} />
                    <StringList label="Banned phrases"
                      items={language.banned_phrases || []}
                      onChange={(items) => setLanguage({ ...language, banned_phrases: items })} />
                    <PairEditor label="Abbreviation expansions" aLabel="abbr" bLabel="expansion"
                      rows={(language.abbreviations || []).map((a) => [a.abbr, a.expansion])}
                      onChange={(rows) => setLanguage({ ...language, abbreviations: rows.map(([abbr, expansion]) => ({ abbr, expansion })) })} />
                  </div>
                </Panel>
              </div>

              {/* RIGHT: preview + save */}
              <div className="space-y-4 lg:sticky lg:top-6 self-start">
                <Panel title="Live preview" subtitle="How a normal report will read.">
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none text-sm text-gray-800 dark:text-gray-200 [&_h2]:text-base [&_h2]:font-bold [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-primary-600 dark:[&_h3]:text-primary-400 [&_p]:my-1"
                    dangerouslySetInnerHTML={{ __html: previewHtml || '<p class="text-gray-400">No sections included.</p>' }}
                  />
                </Panel>

                <div className="flex items-center gap-3">
                  <button
                    onClick={save}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    Save {currentCategory?.display_name}
                  </button>
                  {savedAt && (
                    <span className="flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400">
                      <Check size={15} /> Saved
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

/* ── small presentational helpers ─────────────────────────────────── */

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="bg-white dark:bg-surface-900 border border-gray-200 dark:border-surface-800 rounded-xl p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">{subtitle}</p>}
      {children}
    </section>
  );
}

function Choice({ label, value, options, onChange }: {
  label: string; value: string; options: [string, string][]; onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-gray-600 dark:text-gray-400">{label}</span>
      <div className="flex rounded-lg border border-gray-200 dark:border-surface-800 overflow-hidden">
        {options.map(([val, lbl]) => (
          <button
            key={val}
            onClick={() => onChange(val)}
            className={`px-3 py-1 text-xs transition-colors ${
              value === val
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-surface-850 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-surface-800'
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}

function StringList({ label, items, onChange }: { label: string; items: string[]; onChange: (items: string[]) => void }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
        <button onClick={() => onChange([...items, ''])} className="text-xs text-primary-600 dark:text-primary-400 flex items-center gap-1">
          <Plus size={12} /> add
        </button>
      </div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input
              value={it}
              onChange={(e) => onChange(items.map((x, idx) => (idx === i ? e.target.value : x)))}
              className="flex-1 px-2 py-1 text-sm bg-gray-50 dark:bg-surface-850 border border-gray-200 dark:border-surface-800 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-500">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-gray-400 dark:text-gray-600">None yet.</p>}
      </div>
    </div>
  );
}

function PairEditor({ label, aLabel, bLabel, rows, onChange }: {
  label: string; aLabel: string; bLabel: string; rows: [string, string][]; onChange: (rows: [string, string][]) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
        <button onClick={() => onChange([...rows, ['', '']])} className="text-xs text-primary-600 dark:text-primary-400 flex items-center gap-1">
          <Plus size={12} /> add
        </button>
      </div>
      <div className="space-y-1.5">
        {rows.map(([a, b], i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input
              value={a} placeholder={aLabel}
              onChange={(e) => onChange(rows.map((r, idx) => (idx === i ? [e.target.value, r[1]] : r)))}
              className="flex-1 px-2 py-1 text-sm bg-gray-50 dark:bg-surface-850 border border-gray-200 dark:border-surface-800 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <span className="text-gray-400 text-xs">←</span>
            <input
              value={b} placeholder={bLabel}
              onChange={(e) => onChange(rows.map((r, idx) => (idx === i ? [r[0], e.target.value] : r)))}
              className="flex-1 px-2 py-1 text-sm bg-gray-50 dark:bg-surface-850 border border-gray-200 dark:border-surface-800 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <button onClick={() => onChange(rows.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-500">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-xs text-gray-400 dark:text-gray-600">None yet.</p>}
      </div>
    </div>
  );
}
